// frontend/src/components/common/menuGlyphBars.js
//
// Geometry for the menu (hamburger) glyph, hand-authored instead of taken from
// @phosphor-icons/react.
//
// Every Phosphor glyph is drawn on a 256-unit viewBox with coordinates in
// multiples of 8, so a rendered size of N px scales them by N/256. That lands
// path edges on whole pixels only when N is a multiple of 32 — 18 and 24, the
// sizes the top bar uses, are not. On curved glyphs (Bell, MagnifyingGlass)
// the resulting antialiasing reads as smoothness. On three long horizontal
// bars it reads as blur.
//
// So the glyph is drawn in a viewBox equal to its rendered size: one user unit
// is one CSS pixel, and every bar lands on the grid by construction. No
// rasterization hints, no fractional edges, all three bars identical.
//
// Only correct while the glyph is axis-aligned rectangles — that is the whole
// reason it is hand-authored. Anything needing curves belongs in Phosphor.

// Smallest size whose bars still have a gap between them (see the spread and
// height arithmetic below); under this the glyph collapses into a solid block.
const MIN_SIZE = 12;

/**
 * Bar rectangles for a size×size glyph, in SVG user units.
 *
 * Every returned coordinate is an integer, so at devicePixelRatio 1 each bar
 * covers whole pixels. Proportions track Phosphor's List so the glyph carries
 * the same visual weight as its neighbours in the top bar.
 *
 * @param {number} size — integer box size in px, >= MIN_SIZE
 * @returns {Array<{x: number, y: number, width: number, height: number}>}
 */
export function menuGlyphBars(size) {
  if (!Number.isInteger(size) || size < MIN_SIZE) {
    throw new Error(`MenuGlyph size must be an integer >= ${MIN_SIZE}, got ${size}`);
  }
  // Phosphor's regular weight draws 16-unit strokes: List's own bars are 16
  // units thick, and MagnifyingGlass and Question both cap their strokes with
  // `a8,8` arcs — an 8-unit radius, so 16 across (dist/defs/*.es.js). size/16
  // reproduces that ratio, giving 1px at 18 and 2px at 24. A larger divisor
  // renders the bars heavier than the icons sitting beside them, which is what
  // size/12 did here first.
  const height = Math.max(1, Math.round(size / 16));
  const inset = Math.round(size / 9);
  const middle = Math.round((size - height) / 2);
  const spread = Math.round(size / 4.5);
  return [middle - spread, middle, middle + spread].map((y) => ({
    x: inset,
    y,
    width: size - inset * 2,
    height,
  }));
}
